import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { BASEURL } from '../constant/url';
import { KanbanTask, KanbanTaskAttributes, KanbanTasks, KanbanTaskResponse } from '../interfaces/kanban-task';
import { ID_USER } from '../constant/storage';

export interface KanbanColumn {
    id: 'pending' | 'in-process' | 'completed';
    title: string;
    tasks: KanbanTask[];
}

@Injectable({
    providedIn: 'root'
})
export class KanbanService {
    private readonly _http = inject(HttpClient);
    private readonly API_URL = `${BASEURL}kanban-tasks`;

    private _columns = signal<KanbanColumn[]>([
        { id: 'pending', title: 'Pendiente', tasks: [] },
        { id: 'in-process', title: 'En Proceso', tasks: [] },
        { id: 'completed', title: 'Completado', tasks: [] }
    ]);

    get columns() {
        return this._columns();
    }

    private get currentUserId(): number | null {
        const id = localStorage.getItem(ID_USER);
        return id ? parseInt(id, 10) : null;
    }

    constructor() {
        this.fetchTasks().subscribe({
            error: (err) => console.error('Error initializing Kanban tasks:', err)
        });
    }

    fetchTasks(): Observable<KanbanTask[]> {
        const userId = this.currentUserId;
        if (!userId) return new Observable(sub => sub.next([]));

        return this._http.get<KanbanTasks>(`${this.API_URL}?filters[users_permissions_user][id]=${userId}&pagination[pageSize]=100&sort=createdAt:desc`).pipe(
            map(response => response.data),
            tap(tasks => this.updateLocalState(tasks)),
            catchError(this.handleError)
        );
    }

    private updateLocalState(tasks: KanbanTask[]) {
        const columns: KanbanColumn[] = [
            { id: 'pending', title: 'Pendiente', tasks: tasks.filter(t => t.attributes.status === 'pending') },
            { id: 'in-process', title: 'En Proceso', tasks: tasks.filter(t => t.attributes.status === 'in-process') },
            { id: 'completed', title: 'Completado', tasks: tasks.filter(t => t.attributes.status === 'completed') }
        ];
        this._columns.set(columns);
    }

    addTask(task: Partial<KanbanTaskAttributes>): Observable<KanbanTask> {
        const userId = this.currentUserId;
        const taskData = {
            ...task,
            users_permissions_user: userId
        };

        return this._http.post<KanbanTaskResponse>(this.API_URL, { data: taskData }).pipe(
            map(response => response.data),
            tap(() => this.fetchTasks().subscribe()),
            catchError(this.handleError)
        );
    }

    updateTaskStatus(taskId: number, status: 'pending' | 'in-process' | 'completed'): Observable<KanbanTask> {
        return this._http.put<KanbanTaskResponse>(`${this.API_URL}/${taskId}`, {
            data: { status }
        }).pipe(
            map(response => response.data),
            tap(() => this.fetchTasks().subscribe()),
            catchError(this.handleError)
        );
    }

    updateTask(taskId: number, task: Partial<KanbanTaskAttributes>): Observable<KanbanTask> {
        return this._http.put<KanbanTaskResponse>(`${this.API_URL}/${taskId}`, {
            data: task
        }).pipe(
            map(response => response.data),
            tap(() => this.fetchTasks().subscribe()),
            catchError(this.handleError)
        );
    }

    deleteTask(taskId: number): Observable<void> {
        return this._http.delete<void>(`${this.API_URL}/${taskId}`).pipe(
            tap(() => this.fetchTasks().subscribe()),
            catchError(this.handleError)
        );
    }

    private handleError(error: HttpErrorResponse) {
        let errorMessage = 'Error en el servicio de Kanban; por favor, inténtelo de nuevo más tarde.';
        if (error.error && error.error.error && error.error.error.message) {
            errorMessage = error.error.error.message;
        } else if (error.message) {
            errorMessage = error.message;
        }
        console.error('KanbanService error:', errorMessage, error.error || error);
        return throwError(() => new Error(errorMessage));
    }
}
